import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  inject, model, OnDestroy, OnInit, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { Subject, takeUntil } from 'rxjs';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { KartWebSocketService, BagliCihaz } from './services/kart-websocket.service';

/**
 * Paylaşım sistemi edit görünümü —
 * Bağlı kart okuyuculardan birini seçip kartla ilişkilendir.
 */
@Component({
  selector: 'kart-okuyucu-paylasim-edit',
  standalone: true,
  imports: [CommonModule, FormsModule, NzFormModule, NzInputModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="koe-wrap">

      <!-- Başlık -->
      <div class="koe-baslik-row">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
             stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <rect x="2" y="5" width="20" height="14" rx="2"/>
          <line x1="2" y1="10" x2="22" y2="10"/>
        </svg>
        <span>Kart Okuyucu Seç</span>
        <span class="koe-cihaz-sayi">{{ cihazlar().length }} cihaz</span>
      </div>

      <!-- Cihaz listesi -->
      @if (cihazlar().length === 0) {
        <div class="koe-bos">
          <div class="koe-bos-ikon">📡</div>
          <div class="koe-bos-yazi">Bağlı cihaz bulunamadı</div>
          <div class="koe-bos-alt">Kart okuyucunun sunucuya bağlı olduğundan emin olun</div>
        </div>
      } @else {
        <div class="koe-liste">
          @for (cihaz of cihazlar(); track cihaz.cihaz_kimlik) {
            <button
              class="koe-cihaz-kart"
              [class.secili]="secilenKimlik() === cihaz.cihaz_kimlik"
              (click)="cihazSec(cihaz)">

              <div class="koe-ck-sol">
                <div class="koe-ck-led" [class.aktif]="true"></div>
              </div>

              <div class="koe-ck-icerik">
                <div class="koe-ck-isim">
                  {{ cihaz.cihaz_ismi || cihaz.cihaz_kimlik }}
                </div>
                <div class="koe-ck-kimlik">{{ cihaz.cihaz_kimlik }}</div>
                <div class="koe-ck-durum">
                  {{ cihazDurumu(cihaz) }}
                </div>
              </div>

              @if (secilenKimlik() === cihaz.cihaz_kimlik) {
                <div class="koe-ck-check">✓</div>
              }

            </button>
          }
        </div>
      }

      <!-- Seçilen cihaz özeti -->
      @if (secilenKimlik()) {
        <div class="koe-secili-ozet">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <span>{{ secilenIsim() }} seçildi</span>
          <button class="koe-temizle-btn" (click)="secimTemizle()">✕</button>
        </div>
      }

      <!-- Başlık alanı -->
      <div class="koe-alan">
        <label class="koe-label">Kart Başlığı (opsiyonel)</label>
        <input class="koe-input"
               placeholder="Kart Okuyucu"
               [ngModel]="parametreler()?.baslik || ''"
               (ngModelChange)="guncelle('baslik', $event)" />
      </div>

    </div>
  `,
  styles: [`
    .koe-wrap {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 4px 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }

    /* Başlık satırı */
    .koe-baslik-row {
      display: flex;
      align-items: center;
      gap: 7px;
      font-size: 12px;
      font-weight: 600;
      color: var(--py-metin-ikincil, #888);
    }
    .koe-cihaz-sayi {
      margin-left: auto;
      font-size: 11px;
      background: var(--py-arkaplan-ucuncul, rgba(0,0,0,0.06));
      padding: 2px 7px;
      border-radius: 50px;
    }

    /* Boş durum */
    .koe-bos {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      padding: 20px 12px;
      border: 1px dashed var(--py-sinir, rgba(0,0,0,0.12));
      border-radius: 10px;
      text-align: center;
    }
    .koe-bos-ikon { font-size: 28px; opacity: 0.5; }
    .koe-bos-yazi { font-size: 13px; font-weight: 600; color: var(--py-metin, #333); }
    .koe-bos-alt  { font-size: 11px; color: var(--py-metin-ikincil, #888); }

    /* Cihaz listesi */
    .koe-liste {
      display: flex;
      flex-direction: column;
      gap: 6px;
      max-height: 240px;
      overflow-y: auto;
    }

    /* Cihaz kartı */
    .koe-cihaz-kart {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 9px 12px;
      border-radius: 10px;
      border: 1.5px solid var(--py-sinir, rgba(0,0,0,0.1));
      background: var(--py-arkaplan, #fff);
      cursor: pointer;
      text-align: left;
      transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
      width: 100%;
    }
    .koe-cihaz-kart:hover {
      border-color: rgba(0, 180, 220, 0.5);
      box-shadow: 0 0 0 3px rgba(0, 180, 220, 0.08);
    }
    .koe-cihaz-kart.secili {
      border-color: rgba(0, 200, 200, 0.8);
      background: rgba(0, 200, 200, 0.06);
      box-shadow: 0 0 0 3px rgba(0, 200, 200, 0.12);
    }

    /* LED */
    .koe-ck-sol { display: flex; align-items: center; }
    .koe-ck-led {
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #ccc;
    }
    .koe-ck-led.aktif {
      background: #00e676;
      box-shadow: 0 0 5px #00e676;
      animation: led-pulse 2s ease-in-out infinite;
    }
    @keyframes led-pulse {
      0%,100% { opacity: 1; } 50% { opacity: 0.4; }
    }

    /* İçerik */
    .koe-ck-icerik { flex: 1; min-width: 0; }
    .koe-ck-isim {
      font-size: 13px;
      font-weight: 600;
      color: var(--py-metin, #222);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .koe-ck-kimlik {
      font-size: 10px;
      color: rgba(0, 180, 220, 0.7);
      font-family: 'Courier New', monospace;
      margin-top: 2px;
    }
    .koe-ck-durum {
      font-size: 10px;
      color: var(--py-metin-ikincil, #999);
      margin-top: 2px;
    }

    /* Checkmark */
    .koe-ck-check {
      font-size: 14px;
      color: #00c8c8;
      font-weight: 700;
      flex-shrink: 0;
    }

    /* Seçili özet */
    .koe-secili-ozet {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
      border-radius: 8px;
      background: rgba(0, 200, 180, 0.1);
      border: 1px solid rgba(0, 200, 180, 0.3);
      font-size: 11px;
      color: #00a896;
      font-weight: 600;
    }
    .koe-secili-ozet span { flex: 1; }
    .koe-temizle-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: #00a896;
      font-size: 12px;
      padding: 0 2px;
      opacity: 0.7;
      line-height: 1;
    }
    .koe-temizle-btn:hover { opacity: 1; }

    /* Input alanı */
    .koe-alan { display: flex; flex-direction: column; gap: 4px; }
    .koe-label { font-size: 11px; color: var(--py-metin-ikincil, #888); font-weight: 500; }
    .koe-input {
      width: 100%;
      padding: 7px 10px;
      border-radius: 8px;
      border: 1.5px solid var(--py-sinir, rgba(0,0,0,0.1));
      background: var(--py-arkaplan, #fff);
      color: var(--py-metin, #222);
      font-size: 13px;
      outline: none;
      transition: border-color 0.2s;
      box-sizing: border-box;
    }
    .koe-input:focus { border-color: rgba(0, 180, 220, 0.6); }
    .koe-input::placeholder { color: var(--py-metin-ikincil, #aaa); }
  `],
})
export class KartOkuyucuEditComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy {
  parametreler = model<any>();

  cihazlar      = signal<BagliCihaz[]>([]);
  secilenKimlik  = signal<string>('');
  secilenIsim    = signal<string>('');
  /** cihaz_kimlik → 'kart var' | 'kart yok' */
  private kartDurumMap = new Map<string, boolean>();

  private _destroy$ = new Subject<void>();
  private wsService = inject(KartWebSocketService);
  private cd = inject(ChangeDetectorRef);

  constructor() { super(); }

  ngOnInit() {
    // Mevcut parametrelerden seçili cihazı restore et
    const params = this.parametreler?.() ?? {};
    if (params.cihazKimlik) {
      this.secilenKimlik.set(params.cihazKimlik);
      this.secilenIsim.set(params.cihazIsmi || params.cihazKimlik);
    }

    // Canlı cihaz listesini takip et
    this.wsService.cihazlar$
      .pipe(takeUntil(this._destroy$))
      .subscribe(liste => {
        // kart_okuyucu tipindeki cihazları göster, yoksa hepsini göster
        const filtrelenmis = liste.filter(c =>
          !c.tur || c.tur === 'kart_okuyucu' || c.tur === 'rfid' || c.tur === 'nfc'
        );
        this.cihazlar.set(filtrelenmis.length > 0 ? filtrelenmis : liste);
        this.cd.markForCheck();
      });

    // Kart olaylarını dinle → durumu güncelle
    this.wsService.kartOlayi$
      .pipe(takeUntil(this._destroy$))
      .subscribe(olay => {
        if (olay.durum === 'koyuldu') {
          this.kartDurumMap.set(olay.cihazKimlik, true);
        } else {
          this.kartDurumMap.set(olay.cihazKimlik, false);
        }
        this.cd.markForCheck();
      });

    // WS bağlı değilse bağlan (cihaz listesi için)
    if (!this.wsService.bagli) {
      this.wsService.baglan();
    }
  }

  /** Cihazın anlık durumunu metin olarak döndür */
  cihazDurumu(cihaz: BagliCihaz): string {
    const kartVar = this.kartDurumMap.get(cihaz.cihaz_kimlik);
    if (kartVar === true)  return '💳 Kart okutuldu';
    if (kartVar === false) return '⏳ Kart bekleniyor';
    return '📡 Bağlı — durum bekleniyor';
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._destroy$.next();
    this._destroy$.complete();
  }

  cihazSec(cihaz: BagliCihaz) {
    this.secilenKimlik.set(cihaz.cihaz_kimlik);
    this.secilenIsim.set(cihaz.cihaz_ismi || cihaz.cihaz_kimlik);
    this.parametreler.set({
      ...this.parametreler(),
      cihazKimlik: cihaz.cihaz_kimlik,
      cihazIsmi: cihaz.cihaz_ismi || cihaz.cihaz_kimlik,
    });
  }

  secimTemizle() {
    this.secilenKimlik.set('');
    this.secilenIsim.set('');
    const p = { ...this.parametreler() };
    delete p.cihazKimlik;
    delete p.cihazIsmi;
    this.parametreler.set(p);
  }

  guncelle(alan: string, deger: string) {
    this.parametreler.set({ ...this.parametreler(), [alan]: deger });
  }
}
